/**
 * Parking formations — the user-facing layer over the raw placement fields.
 *
 * The placement engine has four orthogonal knobs (pattern / progression / lanes /
 * coupling) whose combinations are hard to reason about. A "formation" is a named
 * recipe over those knobs — one tap sets all four. This registry is UI-agnostic
 * (plain string enums, identical in the engine `ZoneGeometry` and the DB
 * `area-placement` types), so both the live planner panel and the builder form
 * map a formation onto their own field names.
 *
 * No DB change: a formation just authors the existing columns.
 */
import type { FieldHelpContent } from '@/components/ui/field-help'

export type FormationKey =
  | 'single_row'
  | 'double_row'
  | 'tail_to_tail'
  | 'nose_to_nose'
  | 'nested'
  | 'free_pack'

/** A formation expressed as the engine/DB placement fields it sets. */
export interface FormationRecipe {
  pattern: 'row' | 'double_row' | 'checkerboard' | 'free_stack'
  progression: 'side_by_side' | 'nose_to_tail'
  lanes: number
  coupling: 'tail_to_tail' | 'nested' | 'same_direction' | null
}

export interface FormationDef {
  key: FormationKey
  name: string
  /** One-line description shown in the tile help popover. */
  blurb: string
  recipe: FormationRecipe
}

/**
 * The picker's flat list. Order = display order. `progression` is always
 * side_by_side here (single-file is an Advanced override, not a tile). For the
 * stacked patterns (checkerboard / free_stack) `lanes` is cosmetic — the engine
 * fills the depth — but a sane value is kept for round-tripping.
 */
export const FORMATIONS: FormationDef[] = [
  {
    key: 'single_row',
    name: 'Single Row',
    blurb: 'One line of aircraft, noses aligned the same way.',
    recipe: { pattern: 'row', progression: 'side_by_side', lanes: 1, coupling: null },
  },
  {
    key: 'double_row',
    name: 'Double Row',
    blurb: 'Two parallel rows facing the same direction.',
    recipe: { pattern: 'double_row', progression: 'side_by_side', lanes: 2, coupling: 'same_direction' },
  },
  {
    key: 'tail_to_tail',
    name: 'Tail to Tail',
    blurb: 'Two opposing rows, tails to a shared spine, noses pointing out both sides (aligned).',
    recipe: { pattern: 'double_row', progression: 'side_by_side', lanes: 2, coupling: 'tail_to_tail' },
  },
  {
    key: 'nose_to_nose',
    name: 'Nose to Nose',
    blurb: 'Opposing rows with noses meeting at a center spine, staggered half a slot so noses tuck between the facing aircraft.',
    recipe: { pattern: 'checkerboard', progression: 'side_by_side', lanes: 2, coupling: 'tail_to_tail' },
  },
  {
    key: 'nested',
    name: 'Nested',
    blurb: 'Zipper pack — opposing, staggered rows pulled close so tails tuck into the wing gaps; fills the depth with more rows when there is room.',
    recipe: { pattern: 'free_stack', progression: 'side_by_side', lanes: 2, coupling: 'nested' },
  },
  {
    key: 'free_pack',
    name: 'Free Pack',
    blurb: 'Densest fill — as many same-facing rows as the area depth allows.',
    recipe: { pattern: 'free_stack', progression: 'side_by_side', lanes: 1, coupling: 'same_direction' },
  },
]

const BY_KEY: Record<FormationKey, FormationDef> = Object.fromEntries(
  FORMATIONS.map((f) => [f.key, f])
) as Record<FormationKey, FormationDef>

/** The placement fields for a formation (what to write when a tile is picked). */
export function formationFields(key: FormationKey): FormationRecipe {
  return BY_KEY[key].recipe
}

/** Help-popover content for a formation tile. */
export function formationHelp(def: FormationDef): FieldHelpContent {
  return { title: def.name, summary: def.blurb }
}

const isStacked = (p: FormationRecipe['pattern']) => p === 'checkerboard' || p === 'free_stack'

/**
 * Reverse-map current placement fields to a formation key, or 'custom' for a
 * legacy/hand-tuned combo that no tile represents (the picker then highlights
 * nothing and the Advanced section carries the raw values). Lanes is ignored for
 * stacked patterns since the engine derives row count from the depth there.
 */
export function deriveFormationKey(f: {
  pattern: FormationRecipe['pattern']
  progression: FormationRecipe['progression']
  lanes: number
  coupling: FormationRecipe['coupling']
}): FormationKey | 'custom' {
  for (const def of FORMATIONS) {
    const r = def.recipe
    if (f.pattern !== r.pattern) continue
    if (f.progression !== r.progression) continue
    if ((f.coupling ?? null) !== (r.coupling ?? null)) continue
    if (!isStacked(r.pattern) && Number(f.lanes ?? 1) !== r.lanes) continue
    return def.key
  }
  return 'custom'
}

// ---------------------------------------------------------------------------
// Density — folds Pitch mode (uniform/variable) + Tight-pack rows into one knob.
// ---------------------------------------------------------------------------

export type Density = 'comfortable' | 'compact'

export const DENSITY_LABELS: Record<Density, string> = {
  comfortable: 'Fixed slot',
  compact: 'Wing-to-wing',
}

export const DENSITY_HELP: FieldHelpContent = {
  title: 'Spacing',
  summary: 'How aircraft consume space along each row.',
  options: [
    {
      label: 'Fixed slot',
      description: 'Uniform slots sized to the area’s configured slot width (or the largest aircraft if unset), rows spread evenly — clean, reusable spots.',
    },
    {
      label: 'Wing-to-wing',
      description: 'Each aircraft consumes its own wingspan + safety margin, then the next packs against it — denser, tightest fit.',
    },
  ],
}

/** Pitch mode for a density (`compact` packs to each aircraft's footprint). */
export function pitchForDensity(d: Density): 'uniform' | 'variable' {
  return d === 'compact' ? 'variable' : 'uniform'
}

/** Whether to tight-pack rows by footprint for a density. */
export function footprintRowsForDensity(d: Density): boolean {
  return d === 'compact'
}

/** Read the density back from the underlying fields (any tightness ⇒ compact). */
export function densityFromFields(
  pitchMode: 'uniform' | 'variable' | null | undefined,
  footprintRows?: boolean | null
): Density {
  return pitchMode === 'variable' || footprintRows ? 'compact' : 'comfortable'
}
